// Language detection utility
export const detectUserLanguage = () => {
  // Available languages in the app
  const supportedLanguages = ['de', 'en', 'es', 'it', 'pt'];
  
  // Method 1: Browser language detection
  const browserLanguage = navigator.language || navigator.userLanguage;
  const languageCode = browserLanguage.split('-')[0]; // Extract 'es' from 'es-ES'
  
  // Method 2: Country-based language mapping (unused but kept for reference)
  // const countryLanguageMap = {
  //   'DE': 'de', // Germany
  //   'AT': 'de', // Austria  
  //   'CH': 'de', // Switzerland
  //   'ES': 'es', // Spain
  //   'IT': 'it', // Italy
  //   'GB': 'en', // United Kingdom
  //   'US': 'en', // United States
  //   'CA': 'en', // Canada
  //   'AU': 'en', // Australia
  // };

  // Check if browser language is supported
  if (supportedLanguages.includes(languageCode)) {
    return languageCode;
  }

  // Fallback to German
  return 'de';
};

// Geolocation-based language detection (requires user permission)
export const detectLanguageByLocation = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(detectUserLanguage()); // Fallback to browser language
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Use a reverse geocoding service to get country
          // For demo purposes, we'll use a simple approximation
          // In production, you'd use a proper geocoding API
          const country = await getCountryFromCoordinates(latitude, longitude);
          
          const countryLanguageMap = {
            // German
            'DE': 'de', 'AT': 'de', 'CH': 'de',
            // Spanish (incl. LATAM)
            'ES': 'es', 'AR': 'es', 'MX': 'es', 'CL': 'es', 'PE': 'es', 'CO': 'es', 'UY': 'es', 'PY': 'es', 'BO': 'es', 'VE': 'es', 'EC': 'es',
            // Italian
            'IT': 'it',
            // Portuguese
            'PT': 'pt', 'BR': 'pt',
            // English
            'GB': 'en', 'US': 'en', 'CA': 'en', 'AU': 'en', 'IE': 'en', 'NZ': 'en'
          };

          const detectedLanguage = countryLanguageMap[country] || detectUserLanguage();
          resolve(detectedLanguage);
        } catch (error) {
          console.warn('Geolocation language detection failed:', error);
          resolve(detectUserLanguage());
        }
      },
      (error) => {
        console.warn('Geolocation permission denied:', error);
        resolve(detectUserLanguage()); // Fallback to browser language
      },
      { timeout: 5000 }
    );
  });
};

// Country detection from coordinates using free geocoding API
const getCountryFromCoordinates = async (lat, lng) => {
  try {
    // Use BigDataCloud's free reverse geocoding API (no API key required)
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
    );
    
    if (!response.ok) {
      throw new Error('Geocoding API request failed');
    }
    
    const data = await response.json();
    return data.countryCode;
  } catch (error) {
    console.warn('Geocoding API failed, using geographic approximation:', error);
    
    // Fallback: Simple geographic boundaries (approximate)
    if (lat >= 47.3 && lat <= 55.1 && lng >= 5.9 && lng <= 15.0) return 'DE'; // Germany
    if (lat >= 36.0 && lat <= 43.8 && lng >= -9.3 && lng <= 3.3) return 'ES'; // Spain  
    if (lat >= 35.5 && lat <= 47.1 && lng >= 6.6 && lng <= 18.5) return 'IT'; // Italy
    if (lat >= 49.9 && lat <= 59.0 && lng >= -8.2 && lng <= 1.8) return 'GB'; // UK
    
    return 'DE'; // Default fallback
  }
};

// URL-based language detection (for direct links)
export const detectLanguageFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');
  const supportedLanguages = ['de', 'en', 'es', 'it', 'pt'];
  
  if (langParam && supportedLanguages.includes(langParam)) {
    return langParam;
  }
  
  return null;
};
